import React, { useState, useEffect } from 'react';
import { getEvaluationConfig, updateEvaluationConfig } from '../services/api';
import { useApiKeys } from '../contexts/ApiKeysContext';
import { Key, ExternalLink, AlertTriangle } from 'lucide-react';

const EvaluationSettings = ({ testSetId, onClose }) => {
  const [config, setConfig] = useState({
    autoEvaluationEnabled: true,
    evaluationModel: 'llama-3.1-8b-instant',
    passThreshold: 0.7,
    evaluationCriteria: [],
    customEvaluationPrompt: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { 
    availableModels, 
    loading: modelsLoading, 
    getValidApiKeys, 
    hasExternalApis,
    getInternalModels,
    getExternalModels 
  } = useApiKeys();

  const defaultCriteria = [
    {
      name: 'Relevance',
      description: 'How well does the output address the input query?',
      weight: 0.3
    },
    {
      name: 'Accuracy',
      description: 'Is the information provided correct and factual?',
      weight: 0.25
    },
    {
      name: 'Completeness',
      description: 'Does the output provide a complete response?',
      weight: 0.2
    },
    {
      name: 'Clarity',
      description: 'Is the output clear, well-structured, and easy to understand?',
      weight: 0.15
    },
    {
      name: 'Tone & Style',
      description: 'Is the tone appropriate for the context?',
      weight: 0.1
    }
  ];

  useEffect(() => {
    loadData();
  }, [testSetId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const configData = await getEvaluationConfig(testSetId);
      
      setConfig({
        autoEvaluationEnabled: configData.auto_evaluation_enabled ?? true,
        evaluationModel: configData.evaluation_model || 'llama-3.1-8b-instant',
        passThreshold: configData.pass_threshold || 0.7,
        evaluationCriteria: configData.evaluation_criteria || defaultCriteria,
        customEvaluationPrompt: configData.custom_evaluation_prompt || ''
      });
    } catch (error) {
      console.error('Error loading evaluation config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Include API keys for external models
      const apiKeys = getValidApiKeys();
      const saveData = {
        autoEvaluationEnabled: config.autoEvaluationEnabled,
        evaluationModel: config.evaluationModel,
        passThreshold: config.passThreshold,
        evaluationCriteria: config.evaluationCriteria,
        customEvaluationPrompt: config.customEvaluationPrompt,
        apiKeys: Object.keys(apiKeys).length > 0 ? apiKeys : undefined
      };
      
      await updateEvaluationConfig(testSetId, saveData);
      onClose();
    } catch (error) {
      console.error('Error saving evaluation config:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateCriteria = (index, field, value) => {
    const newCriteria = [...config.evaluationCriteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    setConfig({ ...config, evaluationCriteria: newCriteria });
  };

  const normalizeWeights = () => {
    const totalWeight = config.evaluationCriteria.reduce((sum, criterion) => sum + (criterion.weight || 0), 0);
    if (totalWeight === 0) return;
    
    const normalizedCriteria = config.evaluationCriteria.map(criterion => ({
      ...criterion,
      weight: (criterion.weight || 0) / totalWeight
    }));
    
    setConfig({ ...config, evaluationCriteria: normalizedCriteria });
  };

  const getTotalWeight = () => {
    return config.evaluationCriteria.reduce((sum, criterion) => sum + (criterion.weight || 0), 0);
  };

  const addCriterion = () => {
    const newCriteria = [...config.evaluationCriteria, {
      name: '',
      description: '',
      weight: 0.1
    }];
    setConfig({ ...config, evaluationCriteria: newCriteria });
  };

  const removeCriterion = (index) => {
    const newCriteria = config.evaluationCriteria.filter((_, i) => i !== index);
    setConfig({ ...config, evaluationCriteria: newCriteria });
  };

  const resetToDefaults = () => {
    setConfig({
      ...config,
      evaluationCriteria: defaultCriteria,
      passThreshold: 0.7
    });
  };

  const selectedModel = availableModels.find(m => m.id === config.evaluationModel);
  const isExternalModel = selectedModel?.type === 'external';
  const internalModels = getInternalModels();
  const externalModels = getExternalModels();

  if (loading || modelsLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl bg-white rounded-lg shadow-lg">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading settings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl bg-white rounded-lg shadow-lg">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Auto-Evaluation Settings
          </h3>
          <p className="text-gray-600">
            Configure how your test cases are automatically evaluated using AI.
          </p>
        </div>

        <div className="space-y-6">
          {/* Enable/Disable Auto-evaluation */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Enable Auto-Evaluation</h4>
              <p className="text-sm text-gray-600">
                Automatically evaluate test results using AI after each test run.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.autoEvaluationEnabled}
                onChange={(e) => setConfig({ ...config, autoEvaluationEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {config.autoEvaluationEnabled && (
            <>
              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Evaluation Model
                </label>
                
                <div className="space-y-3">
                  {/* Internal Models Section */}
                  <div>
                    <h4 className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                      Internal Models (Included)
                    </h4>
                    <select
                      value={internalModels.some(m => m.id === config.evaluationModel) ? config.evaluationModel : ''}
                      onChange={(e) => setConfig({ ...config, evaluationModel: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select internal model...</option>
                      {internalModels.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.name} - {model.description}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* External Models Section */}
                  {hasExternalApis() && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide flex items-center">
                        <Key className="h-3 w-3 mr-1" />
                        External Models (Your API Keys)
                      </h4>
                      <select
                        value={externalModels.some(m => m.id === config.evaluationModel) ? config.evaluationModel : ''}
                        onChange={(e) => setConfig({ ...config, evaluationModel: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select external model...</option>
                        {externalModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name} ({model.provider}) - {model.description}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* No External APIs Available */}
                  {!hasExternalApis() && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Key className="h-4 w-4 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-900">Want more models?</h4>
                          <p className="text-xs text-blue-800 mt-1">
                            Add your OpenAI or Anthropic API keys in User Settings to access additional models.
                          </p>
                          <button
                            onClick={() => window.open('/settings', '_blank')}
                            className="text-xs text-blue-700 hover:text-blue-800 mt-1 flex items-center font-medium"
                          >
                            Go to Settings <ExternalLink className="h-3 w-3 ml-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  This model will be used to evaluate the quality of test outputs.
                  {isExternalModel && (
                    <span className="text-blue-600 ml-1">(using your API key)</span>
                  )}
                </p>
              </div>

              {/* External Model Warning */}
              {isExternalModel && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-900">External API Usage</h4>
                      <p className="text-xs text-amber-800 mt-1">
                        This will use your {selectedModel?.provider} API key for evaluations. 
                        Make sure your key has sufficient credits and permissions.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pass Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pass Threshold: {(config.passThreshold * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={config.passThreshold}
                  onChange={(e) => setConfig({ ...config, passThreshold: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Test cases scoring above this threshold will be marked as "PASSED".
                </p>
              </div>

              {/* Evaluation Criteria */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Evaluation Criteria
                    </label>
                    <p className="text-xs text-gray-500">
                      Total weight: {getTotalWeight().toFixed(2)} 
                      {getTotalWeight() !== 1.0 && (
                        <span className="text-orange-600 ml-1">
                          (should equal 1.0)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={normalizeWeights}
                      className="text-sm text-orange-600 hover:text-orange-800"
                      disabled={getTotalWeight() === 0}
                    >
                      Normalize Weights
                    </button>
                    <button
                      onClick={resetToDefaults}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Reset to Defaults
                    </button>
                    <button
                      onClick={addCriterion}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Add Criterion
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {config.evaluationCriteria.map((criterion, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Criterion Name
                            </label>
                            <input
                              type="text"
                              value={criterion.name}
                              onChange={(e) => updateCriteria(index, 'name', e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="e.g., Relevance"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Weight (0.0 - 1.0)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="1"
                              step="0.01"
                              value={criterion.weight}
                              onChange={(e) => updateCriteria(index, 'weight', parseFloat(e.target.value) || 0)}
                              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="0.1"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Current: {(criterion.weight * 100).toFixed(0)}%
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeCriterion(index)}
                          className="ml-3 text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={criterion.description}
                          onChange={(e) => updateCriteria(index, 'description', e.target.value)}
                          rows={2}
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Describe what this criterion evaluates..."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Evaluation Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Evaluation Prompt (Optional)
                </label>
                <textarea
                  value={config.customEvaluationPrompt}
                  onChange={(e) => setConfig({ ...config, customEvaluationPrompt: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter a custom prompt to guide the AI evaluation. Leave empty to use the default evaluation approach."
                />
                <p className="text-xs text-gray-500 mt-1">
                  If provided, this prompt will override the default evaluation criteria and be used to evaluate test outputs.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationSettings; 